import { useEffect, useMemo, useState } from 'react';
import { useLiftTrends } from './useLiftTrends';
import type { ExerciseSeries, ExerciseLift } from './useLiftTrends';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../domain/scoring/categories';
import { LiftTrendChart } from './LiftTrendChart';
import { ExerciseHistoryChart } from './ExerciseHistoryChart';
import './LiftTrendChart.css';
import './TrendCharts.css';

/**
 * Four saturated hues that also separate under colour-vision deficiency is a
 * tight budget once red/green/blue are taken. Hinge's amber sits at deutan
 * dE 7.7 from Pull's green - acceptable only because each series is its own
 * titled panel with a labelled swatch, so colour is never the sole identity
 * cue. Normal-vision separation passes at dE 15.1.
 */
const CATEGORY_COLORS = {
  push: '#8b2500',
  row: '#0f7a52',
  hinge: '#8a5a00',
  squat: '#2f3aa0',
} as const;

/** Stable identity so the detailed useMemo doesn't churn before an id is picked. */
const EMPTY_SERIES: ExerciseSeries = { months: [], lifts: [], weekTicks: [], rangeLabel: '' };

/** The tapped dot, plus where it sits inside the chart so the popup can find it. */
interface LiftSelection {
  lift: ExerciseLift;
  x: number;
  y: number;
}

export interface TrendChartsProps {
  userId: string | undefined;
  /** 'best' shows one chart per category; 'exercise' shows one chosen lift. */
  view: 'best' | 'exercise';
}

/**
 * The progress charts, rendered inline in the history page rather than in a
 * modal. They live in the page's own scroll container, so each chart panel is a
 * fixed height instead of flexing to fill a sheet.
 */
export function TrendCharts({ userId, view }: TrendChartsProps) {
  const { points, exercises, seriesForExercise, isLoading } = useLiftTrends(userId);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string>('');
  const [selectedLift, setSelectedLift] = useState<LiftSelection | null>(null);

  // Default the picker to the first exercise once the history has loaded.
  const exerciseId = selectedExerciseId || exercises[0]?.exerciseId || '';
  const selected = exercises.find((ex) => ex.exerciseId === exerciseId);
  const detailedData = useMemo(
    () => (exerciseId ? seriesForExercise(exerciseId) : EMPTY_SERIES),
    [exerciseId, seriesForExercise],
  );

  // Escape dismisses the lift popup. There is no modal to close behind it now.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedLift(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Derived rather than cleared in an effect: a selection only counts while its
  // dot is still on screen, which drops it when the exercise or the view changes.
  const activeLift =
    selectedLift && detailedData.lifts.some((l) => l.id === selectedLift.lift.id)
      ? selectedLift
      : null;

  const detailStats = useMemo(() => {
    const bests = detailedData.months.filter((m) => m.value !== null).map((m) => m.value as number);
    if (bests.length === 0) return null;
    const change = bests.length > 1 ? bests[bests.length - 1] - bests[0] : null;
    return { best: Math.max(...bests), change, lifts: detailedData.lifts.length };
  }, [detailedData]);

  if (isLoading) {
    return <div className="trends__loading">Loading...</div>;
  }

  if (points.length === 0) {
    return (
      <div className="trends__empty">
        <p>No lifts logged yet.</p>
        <p>Log some workouts and your progress will show up here.</p>
      </div>
    );
  }

  if (view === 'best') {
    const range =
      points.length > 1 ? `${points[0].label}–${points[points.length - 1].label}` : points[0].label;
    return (
      <div className="trends">
        <p className="trends__range">{range}</p>
        <div className="trends__best">
          <LiftTrendChart
            title="Push"
            color={CATEGORY_COLORS.push}
            data={points.map((p) => ({ label: p.label, value: p.push }))}
          />
          <LiftTrendChart
            title="Pull"
            color={CATEGORY_COLORS.row}
            data={points.map((p) => ({ label: p.label, value: p.row }))}
          />
          <LiftTrendChart
            title="Hinge"
            color={CATEGORY_COLORS.hinge}
            data={points.map((p) => ({ label: p.label, value: p.hinge }))}
          />
          <LiftTrendChart
            title="Squat"
            color={CATEGORY_COLORS.squat}
            data={points.map((p) => ({ label: p.label, value: p.squat }))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="trends" onClick={() => setSelectedLift(null)}>
      <div className="trends__picker">
        <label className="sr-only" htmlFor="trend-exercise">
          Exercise
        </label>
        <select
          id="trend-exercise"
          value={exerciseId}
          onChange={(e) => setSelectedExerciseId(e.target.value)}
        >
          {/* Grouped by category with the category as the group label, rather
              than prefixing every name. Categories with nothing logged are
              omitted, since this list only ever holds lifts you have done. */}
          {CATEGORY_ORDER.filter((category) =>
            exercises.some((ex) => ex.category === category),
          ).map((category) => (
            <optgroup key={category} label={CATEGORY_LABELS[category]}>
              {exercises
                .filter((ex) => ex.category === category)
                .map((ex) => (
                  <option key={ex.exerciseId} value={ex.exerciseId}>
                    {ex.displayName}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </div>

      {selected ? (
        <>
          <p className="trends__range">{detailedData.rangeLabel}</p>

          <div className="trends__chart-wrap">
            <ExerciseHistoryChart
              key={selected.exerciseId}
              title={selected.displayName}
              color={CATEGORY_COLORS[selected.category]}
              lifts={detailedData.lifts}
              weekTicks={detailedData.weekTicks}
              selectedLiftId={activeLift?.lift.id}
              onSelectLift={(lift, x, y) =>
                setSelectedLift((current) => (current?.lift.id === lift.id ? null : { lift, x, y }))
              }
            />
            {activeLift && (
              <div
                className={`trends__popup ${activeLift.y < 74 ? 'trends__popup--below' : ''}`}
                style={{ left: activeLift.x, top: activeLift.y }}
                role="status"
              >
                <span className="trends__popup-date">{activeLift.lift.dateLabel}</span>
                <span className="trends__popup-detail">
                  {activeLift.lift.loadLabel} × {activeLift.lift.reps}
                </span>
                <span className="trends__popup-1rm">
                  {Math.round(activeLift.lift.value)} lb est. 1RM
                </span>
              </div>
            )}
          </div>

          {detailStats && (
            <div className="trends__stats">
              <div className="trends__stat">
                <span className="trends__stat-label">Best</span>
                <span className="trends__stat-value">{Math.round(detailStats.best)} lb</span>
              </div>
              <div className="trends__stat">
                <span className="trends__stat-label">Change</span>
                <span className="trends__stat-value">
                  {detailStats.change === null
                    ? '—'
                    : `${detailStats.change >= 0 ? '+' : '−'}${Math.abs(Math.round(detailStats.change))} lb`}
                </span>
              </div>
              <div className="trends__stat">
                <span className="trends__stat-label">Lifts</span>
                <span className="trends__stat-value">{detailStats.lifts}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="trends__empty">
          <p>No exercises logged yet.</p>
        </div>
      )}
    </div>
  );
}
